import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import ProfileAvatar from './ProfileAvatar';

const MAX_UPLOAD_MB = 5;
const CLIENT_MAX_SIDE = 1024;

// Keep these two in step: BOX must equal the Tailwind box of ProfileAvatar's AVATAR_SIZE,
// otherwise the circle would resize when switching between preview and current photo.
const AVATAR_SIZE = 'lg';
const BOX = 'w-24 h-24';

/**
 * Photo field for forms: holds the chosen file locally and previews it. Nothing is
 * uploaded here — the parent posts the file to /{type}/{id}/photo when the form is
 * saved (on creation, once the id exists; on edit, on « Enregistrer »).
 *
 * When `personId` + `photoUpdatedAt` are given, the person's current photo is shown
 * until a new file is picked, so editing shows what is already there.
 *
 * `onSelect(file | null)` hands the (downscaled) file to the parent.
 */
const ProfilePhotoPicker = ({
  onSelect,
  label = 'Photo (facultatif)',
  hint,
  personType,
  personId,
  name,
  photoUpdatedAt,
}) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const downscale = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.max(img.width, img.height);
      if (side <= CLIENT_MAX_SIDE) return resolve(file);
      const scale = CLIENT_MAX_SIDE / side;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], 'photo.jpg', { type: 'image/jpeg' }) : file),
        'image/jpeg',
        0.9
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError('');
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`La photo ne doit pas dépasser ${MAX_UPLOAD_MB} Mo.`);
      return;
    }

    const prepared = await downscale(file);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(prepared));
    onSelect(prepared);
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setError('');
    onSelect(null);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-4">
        {/* One fixed box for all three states — preview, current photo, placeholder —
            so the circle never changes size when a file is picked or cleared. */}
        {preview ? (
          <div className={`${BOX} rounded-full overflow-hidden bg-red-100 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow`}>
            <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
          </div>
        ) : personType && personId ? (
          <ProfileAvatar
            personType={personType}
            personId={personId}
            name={name}
            photoUpdatedAt={photoUpdatedAt}
            size={AVATAR_SIZE}
            className="ring-2 ring-white shadow"
          />
        ) : (
          <div className={`${BOX} rounded-full overflow-hidden bg-red-100 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow`}>
            <FontAwesomeIcon icon={faUser} className="text-red-600 text-2xl" />
          </div>
        )}
        <div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FontAwesomeIcon icon={faCamera} />
              {preview ? 'Changer' : 'Choisir une photo'}
            </button>
            {preview && (
              <button
                type="button"
                onClick={clear}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faTimes} />
                Annuler
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {hint || `JPG, PNG ou WEBP — ${MAX_UPLOAD_MB} Mo maximum.`}
          </p>
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
};

export default ProfilePhotoPicker;
